export interface AuditActor {
  type: 'user' | 'operator' | 'api_key' | 'legacy' | string;
  id: string;
  email?: string;
  label?: string;
  prefix?: string;
}

export interface AuditEntry {
  id: string;
  actor: AuditActor | null;
  orgId: string | null;
  action: string;
  resource: string;
  before: unknown;
  after: unknown;
  origin: string | null;
  correlationId: string | null;
  at: string | null;
}

export interface AuditPage {
  items: AuditEntry[];
  nextCursor: string | null;
}

/** Filtros por familia de acción: `GET /v1/audit?action=` acepta nombre exacto o prefijo `familia.*`. */
export const ACTION_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Todas las acciones' },
  { value: 'qr.destination_change', label: 'Cambio de destino' },
  { value: 'qr.destination_flagged', label: 'Destino rechazado' },
  { value: 'qr.activate', label: 'Activación' },
  { value: 'lot.*', label: 'Lotes y custodia' },
  { value: 'qr.*', label: 'Estado de QR' },
  { value: 'member.*', label: 'Miembros' },
  { value: 'api_key.*', label: 'API keys' },
  { value: 'access.*', label: 'Intentos denegados' },
];

const ACTION_LABELS: Record<string, string> = {
  'qr.activate': 'Activación',
  'qr.create': 'QR creado',
  'qr.destination_change': 'Cambio de destino',
  'qr.destination_flagged': 'Destino rechazado',
  'qr.suspend': 'Suspensión',
  'qr.reactivate': 'Reactivación',
  'qr.deactivate': 'Desactivación',
  'qr.retire': 'Baja',
  'qr.security_block': 'Bloqueo por seguridad',
  'qr.security_unblock': 'Bloqueo levantado',
  'lot.create': 'Lote pedido',
  'lot.printed': 'Lote impreso',
  'lot.custody': 'Asignar custodia',
  'lot.style_change': 'Estilo del lote',
  'kit.create': 'Kit de impresión',
  'member.invite': 'Invitación',
  'member.join': 'Se unió',
  'member.role_change': 'Cambio de rol',
  'member.remove': 'Miembro removido',
  'api_key.create': 'API key creada',
  'api_key.rotate': 'API key rotada',
  'api_key.revoke': 'API key revocada',
  'partner_page.update': 'Página del partner',
  'logo.upload': 'Logo subido',
  'ip_vault.read': 'Lectura de bóveda de IP',
  'org.create': 'Organización creada',
  'org.block': 'Organización bloqueada',
  'org.unblock': 'Organización desbloqueada',
  'qr.version_create': 'Versión nueva',
  'qr.version_primary': 'Versión marcada como principal',
  'qr.version_retire': 'Versión retirada',
  'qr.regen_free': 'Regeneración gratis',
  'qr.regen_quota': 'Regeneración descontada de la cuota',
  'qr.pool_release': 'Liberado al pool',
  'qr.quarantine_start': 'Cuarentena iniciada',
  'qr.quarantine_end': 'Fin de la cuarentena',
  'qr.owner_unlink': 'Código quitado a su dueño',
  'qr.delete': 'QR eliminado',
  'ops.config_change': 'Cambio en la configuración de la operación',
  'bulk.start': 'Acción masiva iniciada',
  'bulk.finish': 'Acción masiva terminada',
  'qr.scan': 'Escaneos',
};

export function actionLabel(action: string): string {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  if (action.startsWith('access.') || action.includes('denied')) return 'Intento denegado';
  return action;
}

export function actorLabel(actor: AuditActor | null, names: Record<string, string> = {}): string {
  if (!actor) return '—';
  const name = actor.email ?? actor.label ?? actor.prefix ?? names[actor.id] ?? actor.id;
  if (actor.type === 'api_key') return `${name} (key)`;
  if (actor.type === 'legacy') return `${name} (credencial legada)`;
  if (actor.type === 'operator') return `${name} (operador)`;
  return name;
}

export function originLabel(origin: string | null): string {
  switch (origin) {
    case 'web':
      return 'Web';
    case 'web_mobile':
    case 'field':
      return 'Web móvil';
    case 'api':
      return 'API';
    case 'job':
    case 'system':
      return 'Sistema';
    default:
      return origin ?? '—';
  }
}

/** Familia del evento en la línea de tiempo de un código (R6.1); da el color del punto del riel. */
export type AuditFamily = 'owner' | 'destination' | 'version' | 'lifecycle' | 'scan' | 'operator' | 'config';

export const FAMILY_LABEL: Record<AuditFamily, string> = {
  owner: 'Dueño',
  destination: 'Destino',
  version: 'Versiones',
  lifecycle: 'Ciclo de vida',
  scan: 'Escaneos',
  operator: 'Operador',
  config: 'Configuración',
};

/** Color del punto por familia (design-spec §3), tokens de DESIGN.md v4. */
export const FAMILY_DOT: Record<AuditFamily, string> = {
  owner: '#F2B90F',
  destination: '#2E9E4F',
  version: '#2F6B57',
  lifecycle: '#A4271C',
  scan: '#A5A49D',
  operator: '#A4271C',
  config: '#5A5F6B',
};

/** Un evento de `GET /v1/audit/qr/{code}` (R6.1). */
export interface QrAuditEntry {
  id: string;
  at: string | null;
  action: string;
  family: AuditFamily | string | null;
  code: string | null;
  actor: AuditActor | string | null;
  origin: string | null;
  reason?: string | null;
  before?: unknown;
  after?: unknown;
  /** Texto ya redactado por el backend cuando lo trae; si no, se arma con `action`. */
  summary?: string | null;
}

export interface QrAuditPage {
  items: QrAuditEntry[];
  nextCursor: string | null;
}

/** `GET /v1/audit/qr/{code}/scans?days=7` (R6.4). */
export interface ScanDay {
  date: string;
  count: number;
}

export interface ScanWeekData {
  days: ScanDay[];
  total: number;
  lastHit?: string | null;
}
